/**
 * ModalHost — usePopup 백엔드 (팝업 스택 마운터).
 *
 * 【역할】
 * - usePopupStore 에 push 된 팝업 엔트리들을 React 트리에 마운트.
 * - 각 팝업의 close(result) 호출 시 Promise resolve 후 스택에서 제거.
 *
 * 【주요 기능】
 * - usePopupStack() 구독 → 엔트리별 Component 렌더 (key=id).
 * - close 콜백은 entry.resolve 호출 후 store.remove(id) 실행.
 * - 앱 루트에 단 1회 마운트 (전역 팝업 호스트).
 *
 * 【Props】
 * - (없음) — 모든 상태는 usePopupStore 에서 가져옴.
 *
 * 【사용 예시】
 * ```tsx
 * // App.tsx 루트에 배치
 * <ModalHost />
 * ```
 */
import { usePopupStack, usePopupStore } from '@/framework/usePopup'

/**
 * ModalHost — usePopupStack 의 모든 팝업 엔트리를 React 트리에 마운트하는 글로벌 호스트.
 * @returns 팝업 컴포넌트 fragment
 */
export default function ModalHost() {
  const stack = usePopupStack()
  const remove = usePopupStore((s) => s.remove)

  return (
    <>
      {stack.map((entry) => {
        const Comp = entry.Component
        /**
         * 팝업 닫기 콜백 — 자식 컴포넌트가 호출.
         * - entry.resolve 로 openPopup() Promise 를 결과값과 함께 종료.
         * - result 미지정 시 null 로 resolve (취소 의미로 활용 가능).
         * - 이후 store 에서 entry 제거 → 트리에서 언마운트.
         */
        const close = (result?: unknown) => {
          entry.resolve(result ?? null)
          remove(entry.id)
        }
        return <Comp key={entry.id} popupProps={entry.props} close={close} />
      })}
    </>
  )
}
